import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import {
  escolhaDoValor,
  useOpcoesClassificacao,
  valorDaClassificacao,
} from '@/lib/classificacao-personalizada';
import { mensagemDeErro } from '@/lib/erros';
import { SelectPesquisavel } from '@/ui/components/select-pesquisavel';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Campo, Input } from '@/ui/primitives/input';
import { Interruptor } from '@/ui/primitives/interruptor';
import {
  type ClassificacaoItem,
  type CondicoesRegra,
  type RegraClassificacaoEntrada,
  regraClassificacaoEntradaSchema,
} from '@fiscoai/contracts';

import { useBuscarCentrosCusto, usePreviaRegra, useSalvarRegra } from './api';
import { CondicoesEditor } from './condicoes-editor';
import { type FormRegra, liquidoDaPrevia, mensagemSalva, type Rascunho } from './formulario-regra';
import { PreviaRegra } from './previa-regra';
import { condicoesPreenchidas } from './textos';

interface Props {
  empresaId: string;
  rascunho: Rascunho;
  aoFechar: () => void;
}

const condicoesDe = (c: FormRegra['condicoes'] | undefined): CondicoesRegra => ({
  ncmPrefixos: c?.ncmPrefixos ?? [],
  cfopsFornecedor: c?.cfopsFornecedor ?? [],
  cnpjsEmitente: c?.cnpjsEmitente ?? [],
  descricaoContem: c?.descricaoContem ?? [],
});

/** Formulário, prévia que acompanha as condições e a gravação com o aviso de quantos itens. */
function useEditorRegra({ empresaId, rascunho, aoFechar }: Props) {
  const form = useForm<FormRegra, unknown, RegraClassificacaoEntrada>({
    resolver: zodResolver(regraClassificacaoEntradaSchema),
    defaultValues: rascunho.valores,
  });
  const condicoes = condicoesDe(useWatch({ control: form.control, name: 'condicoes' }));
  const ativa = useWatch({ control: form.control, name: 'ativa' }) ?? true;
  const previa = usePreviaRegra(empresaId, condicoes, rascunho.regraId ?? undefined);
  const salvar = useSalvarRegra(empresaId);
  const { setFocus } = form;
  useEffect(() => {
    setFocus('nome');
  }, [setFocus]);

  const enviar = form.handleSubmit(async (dados) => {
    try {
      const regra = await salvar.mutateAsync({ ...dados, id: rascunho.regraId });
      const n = previa.data ? liquidoDaPrevia(previa.data) : regra.itens;
      toast.success(mensagemSalva(regra.ativa, n));
      aoFechar();
    } catch (erro) {
      toast.error(mensagemDeErro(erro));
    }
  });
  return { form, ativa, previa, salvando: salvar.isPending, enviar };
}

/**
 * Regra nova começa sem destino (o tipo do formulário não sabe disso): nulo mostra o placeholder.
 */
const destinoOuNulo = (valor: ClassificacaoItem | undefined): ClassificacaoItem | null =>
  valor ?? null;

/** "classificar como [destino] no centro de custo [opcional]" — o fim da frase. */
function Destino({
  form,
  empresaId,
  inicial,
  personalizada,
}: {
  form: ReturnType<typeof useEditorRegra>['form'];
  empresaId: string;
  inicial: Rascunho['centroCusto'];
  personalizada: Rascunho['personalizada'];
}) {
  const buscar = useBuscarCentrosCusto(empresaId);
  const opcoes = useOpcoesClassificacao(empresaId, personalizada);
  const idPersonalizada = useWatch({ control: form.control, name: 'classificacaoPersonalizadaId' });
  const [cc, setCc] = useState(inicial);
  const erro = form.formState.errors.classificacao;
  return (
    <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-start">
      <span className="text-sm font-medium text-texto-suave sm:pt-2">classificar como</span>
      <div className="grid gap-1">
        <Controller
          control={form.control}
          name="classificacao"
          render={({ field }) => (
            <SelectPesquisavel
              aria-label="Destino"
              rotuloBusca="Buscar classificação"
              {...(erro ? { 'aria-invalid': true as const } : {})}
              className="sm:w-60"
              placeholder="Escolha o destino"
              opcoes={opcoes}
              valor={valorDaClassificacao(
                destinoOuNulo(field.value),
                idPersonalizada ? { id: idPersonalizada } : null,
              )}
              aoMudar={(v) => {
                if (!v) return;
                const escolha = escolhaDoValor(v);
                field.onChange(escolha.classificacao);
                form.setValue('classificacaoPersonalizadaId', escolha.classificacaoPersonalizadaId);
              }}
            />
          )}
        />
        {erro && <p className="text-xs text-perigo">Escolha para onde vão os itens</p>}
      </div>
      <span className="text-sm font-medium text-texto-suave sm:pt-2">no centro de custo</span>
      <Controller
        control={form.control}
        name="centroCustoId"
        render={({ field }) => (
          <SelectPesquisavel
            aria-label="Centro de custo (opcional)"
            rotuloBusca="Buscar centro de custo"
            placeholder="Nenhum (opcional)"
            vazio="Nenhum centro de custo ativo — cadastre na aba Centros de custo"
            className="sm:w-56"
            permitirLimpar
            buscar={buscar}
            opcaoSelecionada={cc}
            valor={field.value ?? null}
            aoMudar={(id, opcao) => {
              setCc(opcao);
              field.onChange(id);
            }}
          />
        )}
      />
    </div>
  );
}

/**
 * Editor no lugar da linha: a regra como frase com lacunas, a prévia ao lado e salvar. Abre já
 * preenchido quando vem de um modelo ou de uma regra existente.
 */
export function EditorRegra(props: Props) {
  const { rascunho, aoFechar, empresaId } = props;
  const { form, ativa, previa, salvando, enviar } = useEditorRegra(props);
  const erros = form.formState.errors;
  return (
    <form
      noValidate
      aria-label={rascunho.regraId ? 'Editar regra' : 'Nova regra'}
      className="grid gap-4 lg:grid-cols-3"
      onSubmit={(e) => void enviar(e)}
    >
      <div className="grid content-start gap-4 lg:col-span-2">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <Campo rotulo="Nome da regra" erro={erros.nome ? 'Dê um nome à regra' : undefined}>
            {(a) => <Input {...a} maxLength={80} {...form.register('nome')} />}
          </Campo>
          <Controller
            control={form.control}
            name="ativa"
            render={({ field }) => (
              <Interruptor rotulo="Ligada" ligado={field.value ?? true} aoMudar={field.onChange} />
            )}
          />
        </div>
        <CondicoesEditor
          control={form.control}
          setValue={form.setValue}
          iniciais={condicoesPreenchidas(condicoesDe(rascunho.valores.condicoes))}
          erro={erros.condicoes?.message}
        />
        <Destino
          form={form}
          empresaId={empresaId}
          inicial={rascunho.centroCusto}
          personalizada={rascunho.personalizada}
        />
        <div className="flex flex-wrap justify-end gap-2">
          <Button variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" variante="primario" carregando={salvando}>
            Salvar regra
          </Button>
        </div>
      </div>
      <PreviaRegra
        previa={previa.data}
        condicoesValidas={previa.condicoesValidas}
        carregando={previa.isFetching}
        erro={previa.isError ? mensagemDeErro(previa.error) : null}
        ativa={ativa}
      />
    </form>
  );
}
